import { useState } from 'react';
import type {
  GroupAnalysisCertainty,
  GroupAnalysisEvidence,
  GroupAnalysisFactItem,
  GroupAnalysisOverviewRow,
  GroupAnalysisResponse,
  GroupComplianceReviewStatus,
  GroupDashboardEvidenceRule,
} from '../../api/types.js';
import { EvidenceSheet, type EvidenceSheetData } from './EvidenceSheet.js';
import { ANALYSIS_INELIGIBLE_LABELS, canStartAnalysis } from './analysis-state.js';
import {
  CERTAINTY_LABELS,
  NEXT_STEP_OWNER_LABELS,
  completenessLabel,
  complianceLabel,
  DECISION_CYCLE_LABELS,
  dealSignalLabel,
  INFERRED_LEVEL_LABELS,
  RADAR_AXES,
  factLabel,
  formatDateTime,
  intentLabel,
  lastSpeakerLabel,
  lifecycleStageLabel,
  pipelineValueLabel,
  profileLabel,
  riskLabel,
  runErrorLabel,
  secondsLabel,
  severityLabel,
  stageLabel,
  stageSourceLabel,
  tierLabel,
} from './labels.js';

export type DetailTab = 'intent' | 'computed' | 'strategy' | 'requirements' | 'personality' | 'risk' | 'judgement';
const COMPLIANCE_REVIEW_LABELS: Record<GroupComplianceReviewStatus, string> = { pending: '待核实', confirmed: '已确认', false_positive: '误报' };
const DETAIL_TABS: ReadonlyArray<{ key: DetailTab; label: string }> = [
  { key: 'intent', label: '意向判断' },
  { key: 'computed', label: '推断值' },
  { key: 'strategy', label: '成交策略' },
  { key: 'requirements', label: '需求与订单' },
  { key: 'personality', label: '性格分析' },
  { key: 'risk', label: '风险与合规' },
  { key: 'judgement', label: '业务判断' },
];

/**
 * 单个客户群的分析详情：顶部信息 + 跟进建议卡 + 跟进过程卡 + 五个页签。
 * 数据是后端按 Profile 映射的 Envelope（新旧快照同形），事实区按 envelope.facts
 * 与 API 返回的 labels 动态渲染，不写死任何商家字段；跟进过程来自名单行 followUp（ledger 现算）。
 */
export function AnalysisDetail({ row, analysis, running, isAdmin, evidence, initialTab = 'intent', onBack, onRun, onOpenGroup, onOpenEvidence, onOpenRuleEvidence, onCloseEvidence, onReviewCompliance }: {
  row: Pick<GroupAnalysisOverviewRow, 'conversationId' | 'botWechatRecordId' | 'profile' | 'clusterId' | 'clusterName' | 'inboundCount' | 'latestMessageId' | 'followUp'>;
  analysis: GroupAnalysisResponse | null;
  running: boolean;
  isAdmin: boolean;
  evidence: EvidenceSheetData | null;
  /** 首次渲染落在哪个页签（默认意向判断；SSR 测试按页签渲染）。 */
  initialTab?: DetailTab;
  onBack(): void;
  onRun(): void;
  onOpenGroup(): void;
  onOpenEvidence(evidence: GroupAnalysisEvidence): void;
  onOpenRuleEvidence(messageId: number, rule: GroupDashboardEvidenceRule): void;
  onCloseEvidence(): void;
  /** 风险单核实（仅 admin 显示按钮）；未传时只展示状态。 */
  onReviewCompliance?(flagKey: string, status: GroupComplianceReviewStatus): void;
}) {
  const [tab, setTab] = useState<DetailTab>(initialTab);
  const snapshot = analysis?.snapshot ?? null;
  const envelope = analysis?.envelope ?? null;
  const labels = analysis?.labels ?? null;
  const latestRun = analysis?.latestRun ?? null;
  const latestMessageId = analysis?.latestMessageId ?? row.latestMessageId;
  // 快照水位之后出现新消息，只提示结果可能过期；旧快照仍保留，直到新分析原子发布成功。
  const stale = snapshot !== null && latestMessageId !== null && latestMessageId > snapshot.analyzedThroughMessageId;
  const canRun = !running && analysis !== null && canStartAnalysis(analysis);
  const followUp = row.followUp ?? null;
  // “看原话”只在最后一句是客户说的且知道该消息 id 时可点（规则证据的锚点必须是客户消息）。
  const canOpenLastCustomer = followUp?.lastSpeaker === 'customer' && row.latestMessageId !== null;

  return (
    <section className="analysis-detail" aria-label="智能分析详情">
      <header className="analysis-detail-header">
        <button type="button" className="button ghost" onClick={onBack}>← 名单</button>
        <div className="analysis-detail-title">
          <h2>{row.clusterName || `群 ${row.clusterId}`}</h2>
          <p>
            {profileLabel(row.profile)} · 工作号 {row.botWechatRecordId} · cluster {row.clusterId} · 入站 {row.inboundCount} 条
            {snapshot ? <> · 分析水位 #{snapshot.analyzedThroughMessageId} · 更新于 {formatDateTime(snapshot.createdAt)}</> : null}
          </p>
        </div>
        <div className="analysis-detail-actions">
          {stale ? <span className="analysis-stale">有新消息，结果可能过期</span> : null}
          {running || latestRun?.status === 'running' ? <span className="analysis-running">分析中…</span> : null}
          <button type="button" className="button" onClick={onRun} disabled={!canRun} title={canRun ? '用当前聊天记录重新分析' : '没有新消息或正在分析'}>
            {latestRun?.status === 'failed' ? '重试分析' : '立即分析'}
          </button>
          <button type="button" className="button ghost" onClick={onOpenGroup}>去群聊</button>
        </div>
      </header>

      {followUp ? (
        <article className="analysis-card analysis-follow-up">
          <h3>
            跟进过程
            <small>{isAdmin ? '这单跟得怎么样' : '我的响应'}</small>
            {canOpenLastCustomer && row.latestMessageId !== null ? (
              <button type="button" className="button ghost" onClick={() => onOpenRuleEvidence(row.latestMessageId!, 'missed')}>看原话</button>
            ) : null}
          </h3>
          <div className="analysis-follow-up-grid">
            <div>
              <span>最后一句</span>
              <strong className={followUp.lastSpeaker === 'customer' ? 'waiting' : ''}>{lastSpeakerLabel(followUp.lastSpeaker)}</strong>
              <small>{formatDateTime(followUp.lastMessageAt)}</small>
            </div>
            <div>
              <span>我方首响中位（近 30 天）</span>
              <strong>{secondsLabel(followUp.firstResponseMedianSeconds)}</strong>
            </div>
            <div>
              <span>超 30 分钟次数</span>
              <strong className={followUp.slowReplyCount > 0 ? 'warn' : ''}>{followUp.slowReplyCount}</strong>
            </div>
            <div>
              <span>漏跟</span>
              <strong className={followUp.missedCount ? 'warn' : ''}>{followUp.missedCount ? '是' : '否'}</strong>
            </div>
            <div>
              <span>下一步承诺</span>
              <strong className={followUp.nextStepOverdue ? 'warn' : ''}>
                {envelope?.nextStep?.dueDate ? `${envelope.nextStep.dueDate}${followUp.nextStepOverdue ? ' 已到期' : ' 未到期'}` : '无期限'}
              </strong>
              <small>{envelope?.nextStep ? `${NEXT_STEP_OWNER_LABELS[envelope.nextStep.owner]}：${envelope.nextStep.text}` : ''}</small>
            </div>
          </div>
        </article>
      ) : null}

      {!snapshot || !envelope ? (
        <div className="analysis-empty">
          {!analysis ? '正在加载该群分析…'
            : analysis.eligibility !== 'eligible' ? ANALYSIS_INELIGIBLE_LABELS[analysis.eligibility]
            : latestRun?.status === 'failed'
            ? `暂无可用快照，最近一次分析失败：${runErrorLabel(latestRun.errorCode)}`
            : running || latestRun?.status === 'running' ? '首次分析进行中…' : '尚未分析，点击“立即分析”生成结果'}
        </div>
      ) : (
        <div className="analysis-detail-body">
          {latestRun?.status === 'failed' ? (
            <p className="analysis-note">最近一次更新失败（{runErrorLabel(latestRun.errorCode)}），当前展示的是上一份可用快照。</p>
          ) : null}
          <article className="analysis-card analysis-verdict">
            <h3>跟进建议</h3>
            {envelope.computed ? (
              <div className="analysis-tier-summary">
                <span className={`analysis-tier tier-${envelope.computed.tier}`}>{tierLabel(envelope.computed.tier)}</span>
                <span>客群 {lifecycleStageLabel(envelope.computed.lifecycleStage)}</span>
                <span>在途金额 {pipelineValueLabel(envelope.computed.pipelineValue)}</span>
                <span>需求完整度 {completenessLabel(envelope.computed.requirementCompleteness)}</span>
              </div>
            ) : null}
            <strong>{envelope.verdict.headline}</strong>
            <p>{envelope.verdict.action}</p>
            {envelope.recommendedActions.length > 0 ? (
              <ol>
                {envelope.recommendedActions.map((item, index) => <li key={index}>{item}</li>)}
              </ol>
            ) : null}
            {envelope.taboos.length > 0 ? (
              <div className="analysis-taboos">
                <h4>沟通禁区</h4>
                <TextList items={envelope.taboos.map((taboo) => taboo.text)} />
              </div>
            ) : null}
            <small>建议仅供人工判断，不会自动发送或派单。</small>
          </article>

          <nav className="analysis-detail-tabs" aria-label="详情页签">
            {DETAIL_TABS.map((item) => (
              <button key={item.key} type="button" className={item.key === tab ? 'active' : ''} onClick={() => setTab(item.key)}>{item.label}</button>
            ))}
          </nav>

          {tab === 'intent' ? (
            <div className="analysis-grid">
              <article className="analysis-card">
                <h3>当前阶段</h3>
                <span className="analysis-stage">{stageLabel(labels, envelope.stage.value)}</span>
                {envelope.stageResolution ? <span className="analysis-stage-source">{stageSourceLabel(envelope.stageResolution.source)}</span> : null}
                <Certainty value={envelope.stage.certainty} />
                <EvidenceButtons evidence={envelope.stage.evidence} onOpen={onOpenEvidence} />
                <h3>当前判断</h3>
                <p className="analysis-summary">{envelope.summary}</p>
                {analysis?.inputSummary?.unreadMediaCount ? (
                  <p className="analysis-note">{analysis.inputSummary.unreadMediaCount} 条图片/语音等媒体未解读，未纳入结论。</p>
                ) : null}
              </article>
              <article className="analysis-card">
                <h3>
                  意向 · {intentLabel(envelope.intent.level)}
                  <Certainty value={envelope.intent.certainty} />
                </h3>
                {envelope.intent.score !== null ? <strong className="analysis-intent-score">{envelope.intent.score}</strong> : null}
                <div className="analysis-progress" role="progressbar" aria-valuenow={Math.round(envelope.intent.confidence * 100)} aria-valuemin={0} aria-valuemax={100}>
                  <span style={{ width: `${Math.round(envelope.intent.confidence * 100)}%` }} />
                </div>
                <small>信心 {Math.round(envelope.intent.confidence * 100)}%{envelope.intent.score === null ? ' · 历史快照无分值' : ''}</small>
                <EvidenceButtons evidence={envelope.intent.evidence} onOpen={onOpenEvidence} />
                <div className="analysis-two-col">
                  <div>
                    <h4>成交动力</h4>
                    <TextList items={envelope.intent.drivers} />
                  </div>
                  <div>
                    <h4>当前阻碍</h4>
                    <TextList items={envelope.intent.blockers} />
                  </div>
                </div>
              </article>
            </div>
          ) : null}

          {tab === 'computed' ? (
            <article className="analysis-card">
              <h3>推断值 <small>由代码按事实计算，同一份事实永远同一结论</small></h3>
              {envelope.computed ? (
                <dl className="analysis-requirements">
                  <dt>档位</dt>
                  <dd><span className={`analysis-tier tier-${envelope.computed.tier}`}>{tierLabel(envelope.computed.tier)}</span> <small className="analysis-muted">{envelope.computed.tierReason}</small></dd>
                  <dt>客群阶段</dt>
                  <dd>{lifecycleStageLabel(envelope.computed.lifecycleStage)}{envelope.computed.purchaseCount > 0 ? ` · 成交 ${envelope.computed.purchaseCount} 次` : ''}</dd>
                  <dt>在途金额</dt>
                  <dd>
                    {pipelineValueLabel(envelope.computed.pipelineValue)}
                    {envelope.computed.pipelineValue ? <small className="analysis-muted"> · {envelope.computed.pipelineValue.basis === 'total_amount' ? '按总金额事实' : '按数量 × 品类价带'} · 信心 {Math.round(envelope.computed.pipelineValue.confidence * 100)}%</small> : null}
                  </dd>
                  <dt>需求完整度</dt>
                  <dd>{completenessLabel(envelope.computed.requirementCompleteness)}</dd>
                  <dt>是否已发货</dt>
                  <dd>{envelope.computed.hasShipped ? '是' : '否'}</dd>
                </dl>
              ) : <p className="analysis-muted">历史快照没有推断值；重新分析后生成。</p>}
              <h4>模型推断 <small className="analysis-muted">区间 / 档 · 带置信度与依据（合同 v5 起，旧快照无）</small></h4>
              {envelope.insight?.inferred ? (
                <div className="analysis-inferred">
                  {([
                    ['客户终身价值区间', envelope.insight.inferred.lifetimeValueRange.value, envelope.insight.inferred.lifetimeValueRange],
                    ['成交概率', INFERRED_LEVEL_LABELS[envelope.insight.inferred.dealProbability.value], envelope.insight.inferred.dealProbability],
                    ['流失风险', INFERRED_LEVEL_LABELS[envelope.insight.inferred.churnRisk.value], envelope.insight.inferred.churnRisk],
                    ['复购倾向', INFERRED_LEVEL_LABELS[envelope.insight.inferred.repeatTendency.value], envelope.insight.inferred.repeatTendency],
                    ['决策周期', DECISION_CYCLE_LABELS[envelope.insight.inferred.decisionCycle.value], envelope.insight.inferred.decisionCycle],
                    ['最佳触达时段', envelope.insight.inferred.bestContactWindow.value, envelope.insight.inferred.bestContactWindow],
                  ] as const).map(([label, value, item]) => (
                    <div key={label} className="analysis-inferred-item">
                      <span>{label}</span>
                      <strong>{value}<small>置信度 {Math.round(item.confidence * 100)}%</small></strong>
                      <p>依据：{item.basis}</p>
                    </div>
                  ))}
                </div>
              ) : <p className="analysis-muted">该快照没有模型推断（旧合同）；点“立即分析”重跑后生成。</p>}
            </article>
          ) : null}

          {tab === 'strategy' ? (
            <article className="analysis-card">
              <h3>成交策略建议 <small>由事实、异议、忌讳推出，给人看，不自动发送</small></h3>
              {envelope.insight?.strategy ? (
                <div className="analysis-two-col">
                  <div>
                    <h4>◎ 推荐成交方式</h4>
                    <ol className="analysis-steps">{envelope.insight.strategy.steps.map((step, index) => <li key={index}>{step}</li>)}</ol>
                  </div>
                  <div>
                    <h4>⊘ 沟通中应避免</h4>
                    <TextList items={envelope.insight.strategy.avoid} />
                  </div>
                </div>
              ) : <p className="analysis-muted">该快照没有成交策略（旧合同）；点“立即分析”重跑后生成。</p>}
              {envelope.recommendedActions.length > 0 ? (
                <>
                  <h4>本次分析的推荐动作</h4>
                  <ol className="analysis-steps">{envelope.recommendedActions.map((item, index) => <li key={index}>{item}</li>)}</ol>
                </>
              ) : null}
            </article>
          ) : null}

          {tab === 'personality' ? (
            <article className="analysis-card">
              <h3>客户性格分析 <small>模型基于全部对话的推断，证据充分度即置信度</small></h3>
              {envelope.insight?.personality ? (
                <div className="analysis-personality">
                  <div>
                    <strong className="analysis-ptype">{envelope.insight.personality.type}</strong>
                    <small className="analysis-muted">证据充分度 {Math.round(envelope.insight.personality.confidence * 100)}%</small>
                    <RadarChart radar={envelope.insight.personality.radar} />
                  </div>
                  <div className="analysis-radar-bars">
                    {RADAR_AXES.map((axis) => (
                      <div key={axis.key}>
                        <span>{axis.label}</span>
                        <div className="analysis-progress"><span style={{ width: `${Math.round(envelope.insight!.personality!.radar[axis.key] * 100)}%` }} /></div>
                        <strong>{Math.round(envelope.insight!.personality!.radar[axis.key] * 100)}</strong>
                      </div>
                    ))}
                  </div>
                </div>
              ) : <p className="analysis-muted">该快照没有性格分析（旧合同）；点“立即分析”重跑后生成。</p>}
            </article>
          ) : null}

          {tab === 'requirements' ? (
            <article className="analysis-card">
              <h3>需求与订单 <small>明确=有原话依据，推断=模型推断，未知=未获取</small></h3>
              <dl className="analysis-requirements">
                {envelope.facts.map((fact) => (
                  <Fact key={fact.key} label={factLabel(labels, fact.key)} item={fact} onOpen={onOpenEvidence} />
                ))}
              </dl>
              {envelope.dealSignals.length > 0 ? (
                <>
                  <h4>成交信号</h4>
                  <ul className="analysis-deal-signals">
                    {envelope.dealSignals.map((signal, index) => (
                      <li key={index}>
                        <strong>{dealSignalLabel(signal.type)}</strong>
                        {signal.amount !== null ? <span className="analysis-signal-amount">¥{signal.amount.toLocaleString('zh-CN')}</span> : null}
                        <time>{signal.date}</time>
                        <EvidenceButtons evidence={signal.evidence} onOpen={onOpenEvidence} />
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}
              {envelope.nextStep ? (
                <div className="analysis-next-step">
                  <h4>下一步 · <span className={`analysis-owner owner-${envelope.nextStep.owner}`}>{NEXT_STEP_OWNER_LABELS[envelope.nextStep.owner]}</span></h4>
                  <p>{envelope.nextStep.text}</p>
                  {envelope.nextStep.dueDate ? <p className="analysis-note">承诺期限 {envelope.nextStep.dueDate}</p> : null}
                  <EvidenceButtons evidence={envelope.nextStep.evidence} onOpen={onOpenEvidence} />
                </div>
              ) : null}
              {envelope.objections.length > 0 ? (
                <div>
                  <h4>异议</h4>
                  <TextList items={envelope.objections.map((objection) => `${objection.text}${objection.resolved ? '（已化解）' : ''}`)} />
                </div>
              ) : null}
            </article>
          ) : null}

          {tab === 'risk' ? (
            <article className="analysis-card">
              <h3>风险与合规</h3>
              {envelope.risks.length === 0 && envelope.complianceFlags.length === 0 ? <p className="analysis-muted">暂无</p> : null}
              <ul className="analysis-risks">
                {envelope.risks.map((risk, index) => (
                  <li key={index}>
                    <span className={`analysis-severity severity-${risk.severity}`}>{severityLabel(risk.severity)}</span>
                    <strong>{riskLabel(labels, risk.type)}</strong>
                    <Certainty value={risk.certainty} />
                    <p>{risk.description}</p>
                    <EvidenceButtons evidence={risk.evidence} onOpen={onOpenEvidence} />
                  </li>
                ))}
              </ul>
              {envelope.complianceFlags.length > 0 ? (
                <>
                  <h4>合规风险单 <small>只认我方账号的发言；核实状态按稳定键跨重建保留</small></h4>
                  <ul className="analysis-compliance">
                    {envelope.complianceFlags.map((flag, index) => {
                      const review = analysis?.complianceReviews?.[index] ?? null;
                      const status = review?.status ?? 'pending';
                      return (
                        <li key={index}>
                          <span className="analysis-severity severity-high">{complianceLabel(flag.type)}</span>
                          <strong>{flag.text}</strong>
                          <EvidenceButtons evidence={flag.evidence} onOpen={onOpenEvidence} />
                          <div className="analysis-review">
                            <span className={`analysis-review-status status-${status}`}>{COMPLIANCE_REVIEW_LABELS[status]}</span>
                            {review?.reviewer ? <small>{review.reviewer} · {formatDateTime(review.reviewedAt)}</small> : null}
                            {isAdmin && review && onReviewCompliance ? (
                              <span className="analysis-review-actions">
                                {status !== 'confirmed' ? <button type="button" className="button" onClick={() => onReviewCompliance(review.flagKey, 'confirmed')}>确认属实</button> : null}
                                {status !== 'false_positive' ? <button type="button" className="button ghost" onClick={() => onReviewCompliance(review.flagKey, 'false_positive')}>误报</button> : null}
                                {status !== 'pending' ? <button type="button" className="button ghost" onClick={() => onReviewCompliance(review.flagKey, 'pending')}>撤回</button> : null}
                              </span>
                            ) : null}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </>
              ) : null}
            </article>
          ) : null}

          {tab === 'judgement' ? (
            <article className="analysis-card">
              <h3>业务判断</h3>
              <p className="analysis-summary">{envelope.summary}</p>
              <h4>未决问题</h4>
              {envelope.openQuestions.length === 0 ? <p className="analysis-muted">暂无</p> : null}
              <TextList items={envelope.openQuestions} />
              {analysis?.inputSummary ? (
                <p className="analysis-note">
                  本次输入 {analysis.inputSummary.messageCount} 条消息（#{analysis.inputSummary.firstMessageId ?? '-'} ~ #{analysis.inputSummary.lastMessageId ?? '-'}）
                </p>
              ) : null}
            </article>
          ) : null}
        </div>
      )}

      {evidence ? <EvidenceSheet data={evidence} onClose={onCloseEvidence} /> : null}
    </section>
  );
}

function Fact({ label, item, onOpen }: {
  label: string;
  item: GroupAnalysisFactItem;
  onOpen(evidence: GroupAnalysisEvidence): void;
}) {
  return (
    <>
      <dt>{label}</dt>
      <dd>
        <span className={item.value ? '' : 'analysis-muted'}>{item.value ?? '未获取'}</span>
        <Certainty value={item.certainty} />
        <EvidenceButtons evidence={item.evidence} onOpen={onOpen} />
      </dd>
    </>
  );
}

function Certainty({ value }: { value: GroupAnalysisCertainty }) {
  return <span className={`analysis-certainty certainty-${value}`}>{CERTAINTY_LABELS[value]}</span>;
}

function EvidenceButtons({ evidence, onOpen }: { evidence: GroupAnalysisEvidence[]; onOpen(evidence: GroupAnalysisEvidence): void }) {
  if (evidence.length === 0) return null;
  return (
    <span className="analysis-evidence-buttons">
      {evidence.map((item, index) => (
        <button key={`${item.messageId}-${index}`} type="button" title={item.quote} onClick={() => onOpen(item)}>
          ❝ {index + 1}
        </button>
      ))}
    </span>
  );
}

function TextList({ items }: { items: string[] }) {
  if (items.length === 0) return null;
  return <ul className="analysis-text-list">{items.map((item, index) => <li key={index}>{item}</li>)}</ul>;
}

/**
 * 五维雷达（单系列、无图例）：0.25/0.5/0.75/1 四圈网格，橙色单色面 + 2px 线，轴端直接标注维度与分值；
 * 数值以右侧进度条重复呈现，图形不是唯一读数来源。
 */
function RadarChart({ radar }: { radar: Record<(typeof RADAR_AXES)[number]['key'], number> }) {
  const cx = 100; const cy = 100; const R = 66;
  const point = (index: number, r: number): [number, number] => {
    const angle = -Math.PI / 2 + (index * 2 * Math.PI) / RADAR_AXES.length;
    return [cx + r * Math.cos(angle), cy + r * Math.sin(angle)];
  };
  const ring = (fraction: number) => RADAR_AXES.map((_, index) => point(index, R * fraction).join(',')).join(' ');
  const poly = RADAR_AXES.map((axis, index) => point(index, R * radar[axis.key]).join(',')).join(' ');
  return (
    <svg className="analysis-radar" width="220" height="220" viewBox="0 0 200 200" role="img" aria-label={RADAR_AXES.map((axis) => `${axis.label} ${Math.round(radar[axis.key] * 100)}`).join('，')}>
      {[0.25, 0.5, 0.75, 1].map((fraction) => <polygon key={fraction} points={ring(fraction)} fill="none" stroke="#e5e5e5" />)}
      {RADAR_AXES.map((axis, index) => { const [x, y] = point(index, R); return <line key={axis.key} x1={cx} y1={cy} x2={x} y2={y} stroke="#e5e5e5" />; })}
      <polygon points={poly} fill="rgba(245, 166, 35, .22)" stroke="#f5a623" strokeWidth="2" />
      {RADAR_AXES.map((axis, index) => { const [x, y] = point(index, R * radar[axis.key]); return <circle key={axis.key} cx={x} cy={y} r="3" fill="#f5a623" stroke="#fff" strokeWidth="1.5"><title>{axis.label} {Math.round(radar[axis.key] * 100)}</title></circle>; })}
      {RADAR_AXES.map((axis, index) => { const [x, y] = point(index, R * 1.24); return <text key={axis.key} x={x} y={y + 4} textAnchor="middle" fontSize="11" fill="#737373">{axis.label}</text>; })}
    </svg>
  );
}
